import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Textarea,
  Text,
  Checkbox,
  Heading,
  Divider,
  Icon,
  Input,
  IconButton,
  Select,
} from '@chakra-ui/react';
import { ArrowBackIcon, DownloadIcon } from '@chakra-ui/icons';
import dayjs from 'dayjs';
import CustomRadioGroup from '../../UI/inputs/radio';
import useCustomToast from '../../UI/toast';
import { useAppDispatch, useAppSelector } from '../../../../utils/reduxHooks';
import { getOrdersAction } from '../../../../modules/orders/actions';
import { createRefundAction, uploadAttachmentsAction } from '../../../../modules/refunds/actions';
import { selectAllOrders } from '../../../../modules/orders/selectors';
import { OrderDTO } from '../../../../modules/orders/types';

interface Props {
  onBackClick?: VoidFunction;
}

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_COUNT = 5;

const ReturnForm: React.FC<Props> = ({ onBackClick }) => {
  const toast = useCustomToast();
  const dispatch = useAppDispatch();
  const ordersList = useAppSelector(selectAllOrders);
  const id = useAppSelector(state => state.refunds.uploadId);

  const [selectedOrder, setSelectedOrder] = useState<OrderDTO | null>(null);
  const [reason, setReason] = useState('Брак');
  const [files, setFiles] = useState<File[]>([]);
  const [description, setDescription] = useState('');
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    dispatch(getOrdersAction.request());
  }, [dispatch]);

  useEffect(() => {
    if (files.length > 0 && id) {
      dispatch(uploadAttachmentsAction.request(files));
    }
  }, [files, id]);

  const handleOrderSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOrderId = event.target.value;
    const order = ordersList.find(order => order.pk === selectedOrderId);
    setSelectedOrder(order || null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files ? Array.from(event.target.files) : [];
    const newFiles = selectedFiles.filter(file => {
      if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
        toast(`Файл ${file.name} превышает 5 МБ и не будет добавлен`, 'error');
        return false;
      }
      return true;
    });

    const updatedFiles = [...files, ...newFiles].slice(0, MAX_FILE_COUNT);
    setFiles(updatedFiles);
    if (updatedFiles.length > MAX_FILE_COUNT) {
      toast('Можно загрузить до 5 файлов', 'warning');
    }
  };

  const handleFileClick = () => {
    document.getElementById('fileInput')?.click();
  };

  const handleSubmit = () => {
    if (!agreed) {
      toast('Пожалуйста, подтвердите согласие с политикой конфиденциальности.', 'warning');
      return;
    }

    if (!selectedOrder) {
      toast('Пожалуйста, выберите заказ для загрузки файлов.', 'warning');
      return;
    }

    const formData = {
      reason,
      description: reason === 'Другая причина' ? description : '',
      order_item_id: selectedOrder.order_items[0]?.pk || '',
    };
    dispatch(createRefundAction.request(formData));

    // if (files.length > 0) {
    //   dispatch(uploadAttachmentsAction.request(files));
    // }

    if (onBackClick) {
      setTimeout(() => {
        onBackClick();
      }, 2000);
    }

    toast('Заявка успешно отправлена', 'success');
  };

  return (
    <Flex height="100vh" borderRadius={14}>
      <Box p={8} borderRadius="md" width="100%" maxWidth="700px">
        <Flex>
          {onBackClick && (
            <IconButton
              icon={<ArrowBackIcon />}
              aria-label="Назад"
              onClick={onBackClick}
              variant="ghost"
              mr={2}
              fontSize="1.5rem"
            />
          )}
          <Heading size="lg">Заявка на возврат</Heading>
        </Flex>

        <Text fontSize="sm" color="gray.500" mb={4}>
          {dayjs().format('DD.MM.YYYY')} &nbsp;&bull;&nbsp; {dayjs().format('HH:mm')}
        </Text>

        <Text fontSize="md" fontWeight="bold" mb={2}>
          Выберите заказ для возврата
        </Text>
        <Select placeholder="Выберите заказ" mb={4} onChange={handleOrderSelect}>
          {ordersList.map(order => (
            <option key={order.pk} value={order.pk}>
              Заказ №{order.number} - {order.order_items[0]?.good_name}
            </option>
          ))}
        </Select>

        {selectedOrder && (
          <>
            <Text fontSize="lg" fontWeight="bold">
              {selectedOrder.order_items[0]?.good_name}
            </Text>
            <Text color="gray.600" mb={4}>
              {selectedOrder.order_items[0]?.good_description}
            </Text>
            <Text fontWeight="bold" fontSize="lg" mb={4}>
              {selectedOrder.order_items[0]?.price} ₽ &nbsp;&bull;&nbsp;{' '}
              {selectedOrder.order_items[0]?.amount} шт.
            </Text>
          </>
        )}

        <Divider my={4} />

        <Text fontSize="md" fontWeight="bold" mb={2}>
          Выберите причину возврата
        </Text>
        <CustomRadioGroup value={reason} onChange={setReason} />

        {reason === 'Другая причина' && (
          <Textarea
            placeholder="Коротко опишите свою причину"
            mt={4}
            mb={4}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        )}

        <Box mb={4} mt={4}>
          <Text fontSize="md" fontWeight="bold" mb={2}>
            Загрузите фотографии товара
          </Text>
          <Box
            border="2px dashed"
            borderColor="gray.300"
            borderRadius="md"
            padding={4}
            textAlign="center"
            cursor="pointer"
            onClick={handleFileClick}
            _hover={{ borderColor: 'gray.400', backgroundColor: 'gray.50' }}
          >
            <Icon as={DownloadIcon} boxSize={8} color="gray.500" />
            <Text mt={2} fontSize="sm" color="gray.500">
              Нажмите, чтобы загрузить или перетащите файлы сюда
            </Text>
            <Input
              id="fileInput"
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <Text fontSize="sm" color="gray.400" mt={1}>
              PNG, JPG или GIF, до 5 файлов (макс. 5 МБ каждый)
            </Text>
            {files.map((file, index) => (
              <Text key={index} color="green.400">
                {file.name}
              </Text>
            ))}
          </Box>
        </Box>

        <Checkbox mb={4} isChecked={agreed} onChange={e => setAgreed(e.target.checked)}>
          Нажимая кнопку, Вы соглашаетесь с условиями политики конфиденциальности
        </Checkbox>

        <Button colorScheme="teal" width="100%" onClick={handleSubmit}>
          Отправить заявку
        </Button>
      </Box>
    </Flex>
  );
};

export default ReturnForm;
